/**
 * Manager Timeline page.
 * - Client activated  -> that client's timeline (GET /clients/:id/timeline)
 * - No client active   -> firm-wide activity across all clients (GET /manager/timeline)
 * Mirrors the panel/client-app timeline style (icon bubble + title + meta).
 */
import { useQuery } from '@tanstack/react-query';
import {
  Activity, FileText, Send, Mail, Upload, StickyNote, Clock, MessageSquare,
  Shield, RefreshCw, Loader2, Users, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { useActiveClient } from './active-client';
import { pa } from './base';

const EVENT_CONFIG: Record<string, { icon: any; bg: string; text: string; dot: string }> = {
  document_generated: { icon: FileText, bg: 'bg-indigo-500/15', text: 'text-indigo-500', dot: 'bg-indigo-500' },
  email_sent: { icon: Send, bg: 'bg-blue-500/15', text: 'text-blue-500', dot: 'bg-blue-500' },
  email_received: { icon: Mail, bg: 'bg-green-500/15', text: 'text-green-500', dot: 'bg-green-500' },
  document_uploaded: { icon: Upload, bg: 'bg-slate-500/15', text: 'text-slate-500', dot: 'bg-slate-500' },
  note: { icon: StickyNote, bg: 'bg-amber-500/15', text: 'text-amber-500', dot: 'bg-amber-500' },
  deadline: { icon: Clock, bg: 'bg-red-500/15', text: 'text-red-500', dot: 'bg-red-500' },
  chat: { icon: MessageSquare, bg: 'bg-violet-500/15', text: 'text-violet-500', dot: 'bg-violet-500' },
  client_message: { icon: Send, bg: 'bg-[var(--v4-accent)]/15', text: 'text-[var(--v4-accent)]', dot: 'bg-[var(--v4-accent)]' },
  message_sent: { icon: Send, bg: 'bg-[var(--v4-accent)]/15', text: 'text-[var(--v4-accent)]', dot: 'bg-[var(--v4-accent)]' },
  message_received: { icon: MessageSquare, bg: 'bg-green-500/15', text: 'text-green-500', dot: 'bg-green-500' },
  voice_note: { icon: MessageSquare, bg: 'bg-[var(--v4-accent)]/15', text: 'text-[var(--v4-accent)]', dot: 'bg-[var(--v4-accent)]' },
  compliance_check: { icon: Shield, bg: 'bg-emerald-500/15', text: 'text-emerald-500', dot: 'bg-emerald-500' },
  xero_sync: { icon: RefreshCw, bg: 'bg-sky-500/15', text: 'text-sky-500', dot: 'bg-sky-500' },
};

function eventStyle(type: string) {
  return EVENT_CONFIG[type] || { icon: Activity, bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-muted-foreground' };
}

// Communications sit on the right of the centre line; documents/events on the left.
const COMM_TYPES = new Set(['client_message', 'email_sent', 'email_received', 'message_sent', 'message_received', 'chat', 'voice_note']);
const isComm = (type: string) => COMM_TYPES.has(type);

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ManagerTimelinePage() {
  const { activeClient } = useActiveClient();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ma-timeline', activeClient?.id || 'firm'],
    queryFn: async () => {
      const url = activeClient ? `/clients/${activeClient.id}/timeline?limit=100` : '/manager/timeline?limit=80';
      const r = await apiClient.get(url);
      return r.data.data;
    },
  });

  const events: any[] = data?.events || [];

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-foreground">Timeline</h2>
          {activeClient ? (
            <p className="mt-0.5 truncate text-sm text-[var(--v4-accent)]">{activeClient.name}</p>
          ) : (
            <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><Users className="h-3.5 w-3.5" /> Firm-wide activity</p>
          )}
        </div>
        {activeClient && (
          <button
            onClick={() => navigate(pa(`/client/${activeClient.id}`))}
            className="flex shrink-0 items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50"
          >
            Client card <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--v4-accent)]" /></div>
      ) : isError ? (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          Could not load the timeline. Pull down or try again shortly.
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <Activity className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-foreground">No activity yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {activeClient ? 'Nothing has been recorded for this client yet.' : 'Activity across your clients will show up here.'}
          </p>
        </div>
      ) : activeClient ? (
        /* Per-client: centre line, documents/events on the left, communications on the right. */
        <div>
          <div className="mb-2 flex items-center text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <span className="w-1/2 pr-4 text-right">Documents & events</span>
            <span className="w-1/2 pl-4 text-left">Communications</span>
          </div>
          <div className="relative pt-1">
            <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border" aria-hidden />
            <div className="space-y-3">
              {events.map((e: any) => {
                const type = e.event_type || e.eventType;
                const { icon: Icon, bg, text, dot } = eventStyle(type);
                const when = e.occurred_at || e.created_at;
                const comm = isComm(type);
                const card = (
                  <div className="w-full rounded-xl border border-border bg-card p-2.5 text-left">
                    <div className="flex items-center gap-1.5">
                      <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full', bg)}><Icon className={cn('h-3.5 w-3.5', text)} /></span>
                      <p className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{e.title}</p>
                    </div>
                    {e.description && <p className="mt-1 text-[11px] text-muted-foreground line-clamp-3">{e.description}</p>}
                    <p className="mt-1 text-[10px] text-muted-foreground">{relativeTime(when)}</p>
                  </div>
                );
                return (
                  <div key={e.id} className="relative flex items-start">
                    <div className="flex w-1/2 justify-end pr-4">{!comm && card}</div>
                    <span className={cn('absolute left-1/2 top-2 h-3 w-3 -translate-x-1/2 rounded-full ring-4 ring-background', dot)} />
                    <div className="flex w-1/2 justify-start pl-4">{comm && card}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative space-y-1">
          {/* firm-wide: left rail */}
          <div className="absolute left-[18px] top-2 bottom-2 w-px bg-border" aria-hidden />
          {events.map((e: any) => {
            const type = e.event_type || e.eventType;
            const { icon: Icon, bg, text } = eventStyle(type);
            const when = e.occurred_at || e.created_at;
            return (
              <div key={e.id} className="relative flex gap-3 rounded-xl p-2 transition hover:bg-muted/40">
                <div className={cn('relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-4 ring-background', bg)}>
                  <Icon className={cn('h-4 w-4', text)} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">{e.title}</p>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(when)}</span>
                  </div>
                  {e.description && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-3">{e.description}</p>}
                  {e.client_name && (
                    <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{e.client_name}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
